import { useState, useEffect } from "react";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Clock, AlertTriangle, Check } from "lucide-react";

export default function TracesExplorerPage() {
  const [traces, setTraces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get("/traces").then((r) => { setTraces(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);

  if (loading) return <div className="p-6"><Skeleton className="h-96 bg-white/5" /></div>;

  return (
    <div className="p-6 space-y-4" data-testid="traces-explorer-page">
      <div>
        <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Traces Explorer</h1>
        <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>Pipeline execution traces with span waterfall</p>
      </div>

      <div className="sf-card overflow-hidden" data-testid="traces-list">
        <div className="grid grid-cols-[140px_100px_80px_80px_1fr_60px] gap-2 px-4 py-2.5 text-[9px] uppercase tracking-widest font-semibold" style={{ color: "rgba(30,58,95,0.6)", borderBottom: "1px solid #E8E4DF" }}>
          <span>Trace ID</span><span>Pipeline</span><span>Status</span><span>Duration</span><span>Spans</span><span>Env</span>
        </div>
        {traces.map((t, i) => (
          <button key={t.trace_id} onClick={() => setSelected(t)} className="w-full grid grid-cols-[140px_100px_80px_80px_1fr_60px] gap-2 items-center px-4 py-2.5 text-left hover:bg-black/[0.01] transition-colors animate-fade-up" style={{ borderBottom: "1px solid rgba(255,255,255,0.03)", animationDelay: `${Math.min(i, 15) * 25}ms` }} data-testid={`trace-${t.trace_id}`}>
            <span className="text-[11px] font-mono" style={{ color: "#1E3A5F" }}>{t.trace_id}</span>
            <span className="text-[11px] font-mono" style={{ color: "#6B7280" }}>{t.pipeline_id}</span>
            <Badge className="text-[8px] uppercase font-bold border-0 rounded w-fit" style={{ background: t.status === "error" ? "rgba(190,52,85,0.12)" : "rgba(45,106,79,0.12)", color: t.status === "error" ? "#F87171" : "#4ADE80" }}>{t.status}</Badge>
            <span className="text-[11px] font-mono" style={{ color: "#6B7280" }}>{(t.duration_ms / 1000).toFixed(1)}s</span>
            {/* Mini waterfall */}
            <div className="flex items-center gap-0.5 h-4">
              {(t.spans || []).map((s, si) => {
                const w = Math.max(4, (s.duration_ms / t.duration_ms) * 100);
                return <div key={si} className="h-3 rounded-sm" style={{ width: `${w}%`, minWidth: 4, background: s.status === "error" ? "#EF4444" : "#29B5E8", opacity: 0.4 + (si * 0.1) }} title={`${s.name}: ${s.duration_ms}ms`} />;
              })}
            </div>
            <span className="text-[10px]" style={{ color: "#9CA3AF" }}>{t.environment?.slice(0, 4)}</span>
          </button>
        ))}
      </div>

      {/* Trace detail with waterfall */}
      <Sheet open={!!selected} onOpenChange={() => setSelected(null)}>
        <SheetContent className="w-full sm:max-w-[600px] overflow-auto" style={{ background: "#FFFFFF", borderLeft: "1px solid #E8E4DF" }} data-testid="trace-detail-sheet">
          {selected && (
            <>
              <SheetHeader><SheetTitle className="text-[#2D3142] text-sm font-mono">{selected.trace_id}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="flex items-center gap-3">
                  <Badge className="text-[9px] border-0 rounded" style={{ background: selected.status === "error" ? "rgba(190,52,85,0.12)" : "rgba(45,106,79,0.12)", color: selected.status === "error" ? "#F87171" : "#4ADE80" }}>{selected.status}</Badge>
                  <span className="text-[11px] font-mono" style={{ color: "#6B7280" }}>{selected.pipeline_id}</span>
                  <span className="text-[11px] font-mono" style={{ color: "#6B7280" }}>{(selected.duration_ms / 1000).toFixed(2)}s total</span>
                </div>

                <div>
                  <h4 className="text-[10px] uppercase tracking-widest font-semibold mb-3" style={{ color: "rgba(30,58,95,0.6)" }}>Span Waterfall</h4>
                  <div className="space-y-1.5">
                    {(selected.spans || []).map((span, i) => {
                      const offsetPct = (span.start_offset_ms / selected.duration_ms) * 100;
                      const widthPct = Math.max(3, (span.duration_ms / selected.duration_ms) * 100);
                      const isErr = span.status === "error";
                      return (
                        <div key={i} className="flex items-center gap-3" data-testid={`span-${span.span_id}`}>
                          <span className="text-[10px] font-mono w-[100px] flex-shrink-0 truncate" style={{ color: isErr ? "#F87171" : "#8899AA" }}>{span.name}</span>
                          <div className="flex-1 h-5 rounded relative" style={{ background: "rgba(45,49,66,0.03)" }}>
                            <div className="absolute h-full rounded" style={{ left: `${offsetPct}%`, width: `${widthPct}%`, background: isErr ? "rgba(190,52,85,0.5)" : "rgba(30,58,95,0.4)", border: `1px solid ${isErr ? "rgba(190,52,85,0.6)" : "rgba(30,58,95,0.5)"}` }} />
                          </div>
                          <span className="text-[9px] font-mono w-[55px] text-right flex-shrink-0" style={{ color: "#6B7280" }}>{span.duration_ms}ms</span>
                          {isErr && <AlertTriangle className="w-3 h-3 flex-shrink-0" style={{ color: "#BE3455" }} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
